import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Search, EyeOff, Lock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

type Filter = 'all' | 'hidden' | 'frozen' | 'reported';

export default function AdminListingsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage]     = useState(1);

  const { data, isLoading } = useQuery(
    ['admin-listings', search, filter, page],
    () => apiClient.get<any>('/admin/listings', { params: { search, filter, page, limit: 25 } }),
    { keepPreviousData: true }
  );

  const onSuccess = () => { qc.invalidateQueries('admin-listings'); toast.success('Done'); };

  const hideMut   = useMutation((id: string) => apiClient.post(`/admin/listings/${id}/hide`,   {}), { onSuccess });
  const freezeMut = useMutation((id: string) => apiClient.post(`/admin/listings/${id}/freeze`, {}), { onSuccess });

  const listings: any[] = data?.listings ?? [];
  const total: number   = data?.total ?? 0;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">Listing Management</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-6">{total} listings</p>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search title or seller…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-brand-400" />
        </div>
        {(['all','hidden','frozen','reported'] as Filter[]).map(f => (
          <button key={f} onClick={() => { setFilter(f); setPage(1); }}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f ? 'bg-brand-600 text-white' : 'bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-zinc-400'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">{[...Array(8)].map((_,i) => <div key={i} className="h-10 rounded bg-gray-100 dark:bg-zinc-800 animate-pulse" />)}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-zinc-800 text-xs text-gray-500 dark:text-zinc-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Listing</th>
                <th className="px-4 py-3 text-left">Seller</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Price</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
              {listings.map(l => (
                <tr key={l.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium line-clamp-1">{l.title}</p>
                    <p className="text-xs text-gray-400 dark:text-zinc-600">{new Date(l.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-zinc-400">{l.seller_username}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 capitalize">{l.listing_type}</span>
                  </td>
                  <td className="px-4 py-3 font-medium">{l.price ? `$${l.price}` : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {l.is_hidden  && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400">Hidden</span>}
                      {l.is_frozen  && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">Frozen</span>}
                      {!l.is_hidden && !l.is_frozen && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">Active</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => hideMut.mutate(l.id)} title={l.is_hidden ? 'Unhide' : 'Hide'} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"><EyeOff className="h-4 w-4" /></button>
                      <button onClick={() => freezeMut.mutate(l.id)} title={l.is_frozen ? 'Unfreeze' : 'Freeze'} className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600"><Lock className="h-4 w-4" /></button>
                      <Link to={`/listings/${l.id}`} target="_blank" title="View listing" className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500"><ExternalLink className="h-4 w-4" /></Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-zinc-800 text-sm text-gray-500 dark:text-zinc-500">
          <span>{total} total</span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Prev</button>
            <span className="px-3 py-1">Page {page}</span>
            <button disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded border border-gray-200 dark:border-zinc-700 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
